/**
 * Checkers AI using Minimax with Alpha-Beta Pruning
 */

import { CheckersGame } from './checkersLogic';

const DIFFICULTY_SETTINGS = {
  easy: { depth: 2, randomness: 0.4 },
  medium: { depth: 4, randomness: 0.2 },
  hard: { depth: 6, randomness: 0.1 },
  expert: { depth: 8, randomness: 0 }
};

// Piece values
const PIECE_VALUE = 100;
const KING_VALUE = 300;
const CENTER_BONUS = 10;
const ADVANCE_BONUS = 5;
const BACK_ROW_BONUS = 20;

function evaluateBoard(game, aiColor) {
  let score = 0;
  const boardSize = game.boardSize;
  const opponent = aiColor === 'red' ? 'black' : 'red';
  
  for (let row = 0; row < boardSize; row++) {
    for (let col = 0; col < boardSize; col++) {
      const piece = game.getPiece(row, col);
      if (!piece) continue;
      
      const isAI = piece.color === aiColor;
      const multiplier = isAI ? 1 : -1;
      
      // Base piece value
      let pieceScore = piece.isKing ? KING_VALUE : PIECE_VALUE;
      
      // Center control bonus
      const centerDist = Math.abs(col - boardSize / 2) + Math.abs(row - boardSize / 2);
      pieceScore += (boardSize - centerDist) * CENTER_BONUS / boardSize;
      
      // Advancement bonus (regular pieces)
      if (!piece.isKing) {
        if (piece.color === 'red') {
          pieceScore += (boardSize - 1 - row) * ADVANCE_BONUS;
        } else {
          pieceScore += row * ADVANCE_BONUS;
        }
      }
      
      // Back row defense bonus (regular pieces protecting promotion)
      if (!piece.isKing) {
        if (piece.color === 'red' && row === boardSize - 1) {
          pieceScore += BACK_ROW_BONUS;
        } else if (piece.color === 'black' && row === 0) {
          pieceScore += BACK_ROW_BONUS;
        }
      }
      
      score += pieceScore * multiplier;
    }
  }
  
  // Mobility bonus
  const originalPlayer = game.currentPlayer;
  game.currentPlayer = aiColor;
  const aiMoves = game.getAllValidMoves().length;
  game.currentPlayer = opponent;
  const oppMoves = game.getAllValidMoves().length;
  game.currentPlayer = originalPlayer;
  
  score += (aiMoves - oppMoves) * 5;
  
  // Game over bonus
  if (game.gameOver) {
    if (game.winner === aiColor) {
      score += 10000;
    } else {
      score -= 10000;
    }
  }
  
  return score;
}

function minimax(game, depth, alpha, beta, maximizing, aiColor) {
  if (depth === 0 || game.gameOver) {
    return { score: evaluateBoard(game, aiColor), move: null };
  }
  
  const moves = game.getAllValidMoves();
  
  if (moves.length === 0) {
    return { score: evaluateBoard(game, aiColor), move: null };
  }
  
  let bestMove = moves[0];
  
  if (maximizing) {
    let maxScore = -Infinity;
    
    for (const move of moves) {
      const cloned = game.clone();
      const result = cloned.makeMove(move.from.row, move.from.col, move.to.row, move.to.col);
      
      // Handle multi-capture
      while (result && result.continueCapture) {
        const captures = cloned.getValidMoves(result.piece.row, result.piece.col)
          .filter(m => m.isCapture);
        if (captures.length > 0) {
          const capture = captures[0];
          cloned.makeMove(result.piece.row, result.piece.col, capture.row, capture.col);
        } else {
          break;
        }
      }
      
      const { score } = minimax(cloned, depth - 1, alpha, beta, false, aiColor);
      
      if (score > maxScore) {
        maxScore = score;
        bestMove = move;
      }
      
      alpha = Math.max(alpha, score);
      if (beta <= alpha) break;
    }
    
    return { score: maxScore, move: bestMove };
  } else {
    let minScore = Infinity;
    
    for (const move of moves) {
      const cloned = game.clone();
      const result = cloned.makeMove(move.from.row, move.from.col, move.to.row, move.to.col);
      
      // Handle multi-capture
      while (result && result.continueCapture) {
        const captures = cloned.getValidMoves(result.piece.row, result.piece.col)
          .filter(m => m.isCapture);
        if (captures.length > 0) {
          const capture = captures[0];
          cloned.makeMove(result.piece.row, result.piece.col, capture.row, capture.col);
        } else {
          break;
        }
      }
      
      const { score } = minimax(cloned, depth - 1, alpha, beta, true, aiColor);
      
      if (score < minScore) {
        minScore = score;
        bestMove = move;
      }
      
      beta = Math.min(beta, score);
      if (beta <= alpha) break;
    }
    
    return { score: minScore, move: bestMove };
  }
}

export function getBestMove(game, difficulty = 'medium') {
  const settings = DIFFICULTY_SETTINGS[difficulty] || DIFFICULTY_SETTINGS.medium;
  const aiColor = game.currentPlayer;
  
  const moves = game.getAllValidMoves();
  if (moves.length === 0) return null;
  if (moves.length === 1) return moves[0];
  
  // Add randomness for lower difficulties
  if (Math.random() < settings.randomness) {
    return moves[Math.floor(Math.random() * moves.length)];
  }
  
  const { move } = minimax(game, settings.depth, -Infinity, Infinity, true, aiColor);
  return move;
}

export function executeAIMove(game, difficulty = 'medium') {
  const move = getBestMove(game, difficulty);
  if (!move) return null;
  
  const results = [];
  let result = game.makeMove(move.from.row, move.from.col, move.to.row, move.to.col);
  results.push({ from: move.from, to: move.to });
  
  // Handle multi-capture automatically
  while (result && result.continueCapture) {
    const captures = game.getValidMoves(result.piece.row, result.piece.col)
      .filter(m => m.isCapture);
    
    if (captures.length > 0) {
      // For AI, pick the best capture continuation
      const bestCapture = captures[0];
      result = game.makeMove(result.piece.row, result.piece.col, bestCapture.row, bestCapture.col);
      results.push({ 
        from: { row: result?.piece?.row || captures[0].row, col: result?.piece?.col || captures[0].col }, 
        to: bestCapture 
      });
    } else {
      break;
    }
  }
  
  return results;
}
